import { useEffect, useState } from "react";
import { ExcuseSheet } from "./ExcuseSheet";
import { RunSheet } from "@/components/run/RunSheet";
import { useOfficialWrite, type OfficialSheetProps } from "./shared";
interface PlacementState {
  eligible_heat_rider_ids: string[];
  missing: number[];
  pending: boolean;
  preview: { competitors: { heat_rider_id: string; name: string }[] };
}
export function PlaceSheet(props: OfficialSheetProps) {
  const { heat, competitionId, transport, onClose } = props;
  const base = `/api/competitions/${competitionId}/heats/${heat.id}/placements`;
  const [state, setState] = useState<PlacementState | null>(null);
  const [order, setOrder] = useState<string[]>([]);
  const [direct, setDirect] = useState(false);
  const [excuseJudge, setExcuseJudge] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [loadError, setLoadError] = useState("");
  const { busy, error, write, execute, undo } = useOfficialWrite(props);
  useEffect(() => {
    let active = true;
    void transport
      .get<PlacementState>(base)
      .then((s) => {
        if (active) {
          if (!Array.isArray(s.eligible_heat_rider_ids))
            throw new Error("Placement roster unavailable. Reload this sheet.");
          setState(s);
          setOrder(s.eligible_heat_rider_ids);
        }
      })
      .catch((e) => setLoadError(e.message));
    return () => {
      active = false;
    };
  }, [base, transport]);
  function move(id: string, delta: number) {
    setOrder((rows) => {
      const next = [...rows];
      const at = next.indexOf(id);
      const target = at + delta;
      if (target < 0 || target >= next.length) return rows;
      [next[at], next[target]] = [next[target], next[at]];
      return next;
    });
  }
  if (excuseJudge !== null)
    return (
      <ExcuseSheet
        {...props}
        initialScope="placements"
        initialJudgeNumber={excuseJudge}
        onClose={() => setExcuseJudge(null)}
      />
    );
  return (
    <RunSheet title="Placements" close={onClose}>
      {(error || loadError) && <p role="alert">{error || loadError}</p>}
      {undo}
      {state && (
        <>
          <p>
            {state.missing.length} judge {state.missing.length === 1 ? "list" : "lists"} missing
          </p>
          {state.missing.map((j) => (
            <button key={j} disabled={busy} onClick={() => setExcuseJudge(j)}>
              Excuse Judge {j} for placements
            </button>
          ))}
          <label className="run-toggle">
            <input type="radio" name="method" checked={!direct} onChange={() => setDirect(false)} />
            Place with lists present
          </label>
          <label className="run-toggle">
            <input type="radio" name="method" checked={direct} onChange={() => setDirect(true)} />
            Place directly
          </label>
          {direct && (
            <ol className="official-order">
              {order.map((id, i) => (
                <li key={id}>
                  <span>
                    {i + 1}. {state.preview.competitors.find((r) => r.heat_rider_id === id)?.name}
                  </span>
                  <button
                    disabled={busy || i === 0}
                    aria-label={`Move ${i + 1} up`}
                    onClick={() => move(id, -1)}
                  >
                    ↑
                  </button>
                  <button
                    disabled={busy || i === order.length - 1}
                    aria-label={`Move ${i + 1} down`}
                    onClick={() => move(id, 1)}
                  >
                    ↓
                  </button>
                </li>
              ))}
            </ol>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void write(() =>
                execute("placement", {
                  method: "head_judge",
                  reason: reason.trim(),
                  ...(direct
                    ? { ordered_heat_rider_ids: order }
                    : { force_missing: state.missing }),
                }),
              );
            }}
          >
            <label>
              Reason
              <textarea required value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <p>Confirm the final order before placing this heat.</p>
            <button
              className="run-primary"
              disabled={busy || !reason.trim() || (!direct && !order.length)}
            >
              Confirm placement
            </button>
          </form>
        </>
      )}
    </RunSheet>
  );
}
